import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ShieldCheck, ShieldAlert, ChevronDown, ChevronUp, RefreshCw, ClipboardCopy, Loader2,
} from 'lucide-react';

/**
 * EnvAuditPanel — one-glance environment secret health for the admin dashboard.
 *
 * GET /api/admin/env-audit returns { audited_at, findings[], summary{critical, high, medium, low, clean} }.
 * We render a compact card with severity totals + collapsible detail list.
 */

const SEVERITY_STYLES = {
  critical: {
    label: 'Critical',
    bg: 'bg-rose-500/15 border-rose-500/40 text-rose-200',
    dot: 'bg-rose-500',
  },
  high: {
    label: 'High',
    bg: 'bg-amber-500/15 border-amber-500/40 text-amber-200',
    dot: 'bg-amber-500',
  },
  medium: {
    label: 'Medium',
    bg: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200',
    dot: 'bg-yellow-500',
  },
  low: {
    label: 'Low',
    bg: 'bg-slate-500/10 border-slate-500/30 text-slate-300',
    dot: 'bg-slate-400',
  },
};

const EnvAuditPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // iter132 — rotation-wizard copy state
  const [copying, setCopying] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const res = await api.get('/admin/env-audit');
      setData(res.data);
    } catch (e) {
      // swallow — presence of an error just means we can't audit right now
      setData({ findings: [], summary: {}, total_keys_checked: 0, error: true });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // iter132 — Env Rotation Wizard.
  // Fetches the current AUDITED_KEYS values as dotenv-format text and pushes
  // to navigator.clipboard so the admin can paste directly into the Emergent
  // production Secret Manager. Backend REFUSES to include any value that
  // still matches a historical leak hash, so this can't propagate a known
  // compromised secret to production.
  const handleCopyDotenv = async () => {
    if (copying) return;
    setCopying(true);
    try {
      const res = await api.get('/admin/env-audit/export-dotenv');
      const text = res.data?.dotenv || '';
      if (!text) {
        toast.error('Nothing to copy — all audited keys are blank or still leaked.');
        return;
      }
      // Modern clipboard API (needs HTTPS + user gesture). Falls back to a
      // temp textarea for older browsers or non-secure contexts.
      try {
        await navigator.clipboard.writeText(text);
      } catch (_clipErr) {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      const skipped = res.data?.skipped_leaked || [];
      const skippedBlank = res.data?.skipped_blank || [];
      const parts = [`Copied ${res.data.count} env var${res.data.count === 1 ? '' : 's'} to clipboard`];
      if (skipped.length > 0) {
        parts.push(`(refused to export ${skipped.length} still-leaked: ${skipped.join(', ')})`);
      }
      if (skippedBlank.length > 0) {
        parts.push(`(skipped ${skippedBlank.length} blank)`);
      }
      toast.success(parts.join(' '), {
        description:
          'For most cases you just need to click Deploy — Emergent bundles .env with the deployment. Only paste manually if support instructed you to.',
        duration: 8000,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Copy failed — check your admin session.');
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <Card data-testid="env-audit-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const summary = data?.summary || {};
  const findings = data?.findings || [];
  const allClean = findings.length === 0 && !data?.error;
  const worstSev =
    (summary.critical > 0 && 'critical') ||
    (summary.high > 0 && 'high') ||
    (summary.medium > 0 && 'medium') ||
    (summary.low > 0 && 'low') ||
    'clean';

  // iter131 — environment context (preview vs. production). The endpoint
  // returns `public_base_url_current` (from the PUBLIC_BASE_URL env var);
  // preview URLs contain `preview.emergentagent.com` or `.emergent.host`.
  // Anything else with a value is treated as production.
  const auditedUrl = data?.public_base_url_current || '';
  const isPreview =
    auditedUrl.includes('preview.emergentagent.com') ||
    auditedUrl.includes('.emergent.host') ||
    auditedUrl.includes('localhost');
  const isProduction = !!auditedUrl && !isPreview;
  const envLabel = auditedUrl ? (isProduction ? 'PRODUCTION' : 'PREVIEW') : 'this pod';

  return (
    <Card
      className={
        allClean
          ? 'border-emerald-500/40 bg-emerald-500/5'
          : worstSev === 'critical'
          ? 'border-rose-500/50 bg-rose-500/5'
          : 'border-amber-500/40 bg-amber-500/5'
      }
      data-testid="env-audit-panel"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {allClean ? (
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
            ) : (
              <ShieldAlert
                className={
                  worstSev === 'critical' ? 'h-5 w-5 text-rose-400' : 'h-5 w-5 text-amber-400'
                }
              />
            )}
            <CardTitle className="text-base">Environment Secrets Health</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={refreshing}
            data-testid="env-audit-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          {/* iter132 — Env Rotation Wizard copy button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopyDotenv}
            disabled={copying}
            data-testid="env-audit-copy-dotenv"
            title="Copy audited env vars as dotenv format (only needed for out-of-band prod edits — for the standard flow, just Deploy)"
          >
            {copying ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ClipboardCopy className="h-4 w-4" />
            )}
          </Button>
        </div>
        <CardDescription>
          {data?.total_keys_checked || 0} keys audited · last checked{' '}
          {data?.audited_at ? new Date(data.audited_at).toLocaleTimeString() : '—'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* iter131 — environment context banner. Makes it unambiguous which
            environment the tripwire is scanning. Prevents the "I rotated in
            preview but production still shows criticals" confusion. */}
        {auditedUrl && (
          <div
            className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-[11px] font-medium ${
              isProduction
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-100'
                : 'border-sky-500/40 bg-sky-500/10 text-sky-100'
            }`}
            data-testid="env-audit-env-banner"
          >
            <span
              className={`inline-block h-1.5 w-1.5 rounded-full ${
                isProduction ? 'bg-rose-400' : 'bg-sky-400'
              }`}
            />
            Auditing&nbsp;<strong className="tracking-wide">{envLabel}</strong>
            &nbsp;deployment at&nbsp;
            <code className="font-mono text-[10px] bg-black/30 px-1.5 py-0.5 rounded truncate max-w-[260px]">
              {auditedUrl}
            </code>
          </div>
        )}

        {allClean && (
          <p className="text-sm text-emerald-300" data-testid="env-audit-clean">
            All secrets healthy — no leak patterns, no historical exposure detected.
          </p>
        )}

        {!allClean && (
          <div className="flex flex-wrap gap-2" data-testid="env-audit-summary">
            {['critical', 'high', 'medium', 'low'].map((sev) => {
              const count = summary[sev] || 0;
              if (count === 0) return null;
              const s = SEVERITY_STYLES[sev];
              return (
                <Badge
                  key={sev}
                  className={`border ${s.bg}`}
                  data-testid={`env-audit-sev-${sev}`}
                >
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot} mr-1.5`} />
                  {count} {s.label}
                </Badge>
              );
            })}
            {summary.clean > 0 && (
              <Badge className="border border-emerald-500/40 bg-emerald-500/15 text-emerald-200">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 mr-1.5" />
                {summary.clean} clean
              </Badge>
            )}
          </div>
        )}

        {/* iter131 → iter133 — "How to fix on production" callout. Rendered
            ONLY when critical findings exist. On Emergent there is NO separate
            self-service secret-manager UI (as of Feb 2026); env vars are
            bundled into the deployment. So the correct fix is: rotate in
            preview .env → click Deploy in the Emergent chat → the new values
            ship with the next deployment. */}
        {summary.critical > 0 && (
          <div
            className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-50"
            data-testid="env-audit-prod-fix-callout"
          >
            <div className="mb-1.5 flex items-center gap-1.5 font-semibold text-rose-200">
              <ShieldAlert className="h-3.5 w-3.5" /> How to fix on production
            </div>
            <ol className="list-decimal list-inside space-y-1 text-slate-200/90">
              <li>
                Rotate the flagged values in your <strong>preview</strong>{' '}
                <code className="rounded bg-black/30 px-1 text-slate-300">/app/backend/.env</code>{' '}
                (ask the builder chat to rotate them, or edit the file directly).
              </li>
              <li>
                Refresh this panel first — confirm your <strong>preview</strong> banner (top of card) now shows{' '}
                <em>0 criticals</em>.
              </li>
              <li>
                In your Emergent chat, click the <strong>Deploy</strong> button (top of chat) and choose{' '}
                <strong>Deploy Now / Update Deployment</strong>. Emergent bundles the current preview{' '}
                <code className="rounded bg-black/30 px-1 text-slate-300">.env</code> into the deployment automatically.
              </li>
              <li>
                Wait 5–15 min for the redeploy to finish, then refresh{' '}
                <code className="rounded bg-black/30 px-1 text-slate-300">/admin</code> on your live site — critical count should drop to 0.
              </li>
            </ol>
            <p className="mt-2 text-[10px] text-slate-400">
              Note: on Emergent there is no separate self-service Secret Manager UI — env vars ship as part of
              the deployment. If you need out-of-band env changes without a full redeploy, email{' '}
              <span className="text-slate-300">support@emergent.sh</span> with your job ID.
            </p>
          </div>
        )}

        {findings.length > 0 && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              className="px-0 h-auto text-xs text-slate-400 hover:text-slate-200"
              data-testid="env-audit-toggle"
            >
              {expanded ? (
                <>
                  Hide details <ChevronUp className="h-3 w-3 ml-1" />
                </>
              ) : (
                <>
                  Show {findings.length} finding{findings.length === 1 ? '' : 's'}{' '}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </>
              )}
            </Button>

            {expanded && (
              <ul
                className="text-xs space-y-1.5 border-t border-slate-700 pt-3 mt-2"
                data-testid="env-audit-detail-list"
              >
                {findings.map((f, idx) => {
                  const s = SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.low;
                  return (
                    <li
                      key={`${f.key}-${idx}`}
                      className="flex items-start gap-2"
                      data-testid={`env-audit-finding-${f.key}`}
                    >
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${s.dot} mt-1.5`} />
                      <span>
                        <strong className="text-slate-200">{f.label}</strong>
                        <span className="text-slate-500"> ({f.key})</span>
                        <br />
                        <span className="text-slate-400">{f.reason}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {data?.error && (
          <p className="text-xs text-rose-400" data-testid="env-audit-error">
            Audit endpoint returned an error. Backend may be down or you may have lost your admin
            session — try refreshing the panel.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default EnvAuditPanel;
